import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { X, Download } from 'lucide-react';

/**
 * PWA Install Prompt — shows "Add to Home Screen" after 30 seconds on mobile.
 * Listens for the `beforeinstallprompt` event. Dismissed by user or auto-hidden
 * if already installed (standalone mode).
 */
export function PWAInstallPrompt() {
  const [show, setShow] = useState(false);
  const deferredPromptRef = useRef(null);

  useEffect(() => {
    // Already in standalone mode (installed)?
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    const handler = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Show after 30 seconds
    const timer = setTimeout(() => {
      if (deferredPromptRef.current) {
        setShow(true);
      }
    }, 30000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPromptRef.current) return;
    deferredPromptRef.current.prompt();
    const result = await deferredPromptRef.current.userChoice;
    if (result.outcome === 'accepted') {
      setShow(false);
    }
    deferredPromptRef.current = null;
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-sm animate-slideUp" data-testid="pwa-install-prompt">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#0d1f6e' }}>
          <Download size={18} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900">Install Sturdfix WMS</div>
          <div className="text-xs text-gray-500">Add to Home Screen for quick access</div>
        </div>
        <Button onClick={handleInstall} size="sm" className="h-9 px-4 text-xs font-semibold" style={{ backgroundColor: '#0d1f6e' }} data-testid="pwa-install-btn">
          Install
        </Button>
        <button onClick={() => setShow(false)} className="p-1 hover:bg-gray-100 rounded" data-testid="pwa-dismiss">
          <X size={14} className="text-gray-400" />
        </button>
      </div>
    </div>
  );
}
